import { Suspense, useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { saveCarrierGuid } from 'shared/auth/AuthToken';
import { identifyAnalytics } from 'shared/helpers/Analytics';
import { identifyUser } from 'shared/helpers/Logger';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { lazyImport } from 'shared/routing/LazyRoute';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { basePath } from 'shared/utils/RouterUtils';
import { SuperPayErrorPage } from './profile/superpay/SuperPayErrorPage';
import { DriverInfoPage } from './webview/driver-info/DriverInfoPage';
import { DriverFeedbackPage } from './webview/driver-feedback/DriverFeedbackPage';
import { DriverPreferencesPage } from './webview/driver-preferences/DriverPreferencesPage';
import { InstantTransferWebViewRoute } from './webview/instant-transfer/InstantTransferWebViewRoute';
import { InstantTransferSettleWebViewDialog } from './webview/instant-transfer-settle/InstantTransferSettleWebViewDialog';
import { NotificationsPage } from './webview/notifications/NotificationsPage';
import { profileWebViewRoutes } from './webview/profile/ProfileWebViewRoutes';
import { superPayWebViewRoutes } from './webview/profile/superpay/ProfileSuperPayPage';
import { SubscriptionPage } from './webview/settings/SubscriptionPage';
import { BotpressWebchat } from './webview/webchat/BotpressWebchat';
import { hideIntercomLauncher } from 'shared/helpers/Intercom';
import { AppContainer } from 'shared/app/AppContainer';
import { onboardingPacketsRoutes } from './webview/onboarding-packets/OnboardingPacketsRoutes';

const SuperCardPage = lazyImport(
  () => import('shared/modules/super-card/SuperCardPage'),
  'SuperCardPage',
);

const SuperCardMenuPage = lazyImport(
  () => import('shared/modules/super-card/SuperCardMenuPage'),
  'SuperCardMenuPage',
);

const SuperCardExperimentWebView =
  import.meta.env.VITE_APP_TARGET !== 'production'
    ? lazyImport(
        () => import('shared/modules/super-card/SuperCardExperiment'),
        'SuperCardExperiment',
      )
    : null;

const router = createBrowserRouter(
  [
    {
      element: (
        <AppContainer>
          <Outlet />
        </AppContainer>
      ),
      children: [
        { path: '/driver-preferences', element: <DriverPreferencesPage /> },
        { path: '/superpay/error', element: <SuperPayErrorPage /> },
        { path: '/support', element: <BotpressWebchat /> },
        {
          path: '/superpay/instant-transfer',
          element: <InstantTransferWebViewRoute />,
        },
        {
          path: '/superpay/instant-transfer-settle',
          element: <InstantTransferSettleWebViewDialog />,
        },
        {
          path: '/',
          element: <CarrierWebviewAppLayout />,
          children: [
            ...profileWebViewRoutes,
            ...superPayWebViewRoutes,
            ...onboardingPacketsRoutes,
            { path: 'notifications', element: <NotificationsPage /> },
            { path: 'driver-info', element: <DriverInfoPage /> },
            { path: 'driver-feedback', element: <DriverFeedbackPage /> },
            {
              path: 'settings/subscription/:action?',
              element: <SubscriptionPage />,
            },

            ...(SuperCardExperimentWebView
              ? [
                  {
                    path: 'super-card-experiment',
                    element: (
                      <Suspense fallback={<ContentProgressIndicator />}>
                        <SuperCardExperimentWebView />
                      </Suspense>
                    ),
                  },
                ]
              : []),
          ],
        },
      ],
    },
    {
      element: (
        <AppContainer disableBotpress={true}>
          <Outlet />
        </AppContainer>
      ),
      children: [
        {
          path: 'super-card',
          element: (
            <Suspense fallback={<ContentProgressIndicator />}>
              <SuperCardPage />
            </Suspense>
          ),
        },
        {
          path: 'super-card-menu/:section?',
          element: (
            <Suspense fallback={<ContentProgressIndicator />}>
              <SuperCardMenuPage />
            </Suspense>
          ),
        },
      ],
    },
  ],
  { basename: basePath },
);

export function CarrierWebviewApp() {
  useEffect(() => {
    hideIntercomLauncher();
  }, []);

  return <RouterProvider router={router} />;
}

function CarrierWebviewAppLayout() {
  const { data: settings } = useCarrierSettings();

  useEffect(() => {
    if (settings?.carrier.guid) {
      saveCarrierGuid(settings.carrier.guid);
    }

    if (settings) {
      identifyUser({
        id: settings.user.guid,
        carrier_guid: settings.carrier.guid,
        carrier_name: settings.carrier.name,
      });
      identifyAnalytics({
        guid: settings.user.guid,
        hash: settings.user.user_hash,
        accountType: settings.company_type,
        company: {
          id: settings.carrier.guid,
          guid: settings.carrier.guid,
          name: settings.carrier.name,
          is_super: settings.carrier.verification_status === 'approved',
          superpay_onboarding_status: settings.superpay.verification,
          is_fmcsa_phone_verified: settings.is_fmcsa_phone_verified,
        },
      });
    }
  }, [settings]);

  if (!settings?.user) {
    return <SplashScreen />;
  }

  return <Outlet />;
}
